/**
 * Live Operations — the workspace an operator sits in while work is running.
 *
 * The terminal is a region of this page, not a modal over it. That is the whole
 * design: watching a session and reading what the session changed are the same
 * activity, and a modal forces the operator to choose one. So the terminals
 * occupy the top of the right-hand column and the inspector sits under them,
 * both visible at once, and "fullscreen" expands the terminals in place rather
 * than opening something new.
 *
 * The session kind is the loudest label on the screen. An OBSERVED session is
 * one Kimene found but does not own — there is no controllable PTY behind it,
 * and no amount of interface can produce one. So an observed session shows the
 * hint, offers no mode control, and is handed to the terminal pane in OBSERVE
 * mode. Kimene must never imply it can steer something it cannot.
 *
 * The inspector shows what /sessions/{id} returns and nothing else. It used to
 * carry seven tabs — messages, tool activity, changed files, a diff, a test
 * summary, resource metrics — none of which appear on api.SessionDetailResponse
 * under any name. Every one of those panels rendered its empty state on every
 * session forever, which reads as "this session touched no files" rather than
 * "Kimene does not collect that". A checkpoints tab was the last survivor of
 * that pattern — there is no Checkpoint entity, field or route server-side, so
 * it too rendered an empty state forever and has been removed. What the endpoint
 * does carry is the session itself and whether a terminal can be proxied to it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Copy,
  Info,
  MoreVertical,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Plus,
  KeyRound,
  RefreshCw,
  RotateCcw,
  Search,
  Square,
  Terminal,
  Trash2,
  X,
  GripVertical,
  ListChecks,
  CheckCheck,
} from 'lucide-react'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import clsx from 'clsx'
import { api } from '@/api/client'
import { Icon } from '@/components/icon'
import {
  Page,
  Panel,
  Badge,
  Button,
  StatusDot,
  EmptyState,
  KeyValue,
  Meter,
} from '@/components/primitives'
import {
  QueryState,
  RowCheckbox,
  SelectField,
  Notice,
  useApiErrorMessage,
} from '@/components/controls'
import { useConfirm } from '@/components/confirm'
import { AccountUsagePanel, type UsageWindow } from '@/components/AccountUsage'
import { SignInConfirm } from './signInConfirm'
import { StartSessionDialog } from './StartSessionDialog'
import { SwitchAccountDialog } from './SwitchAccountDialog'
import { useT, useLocale } from '@/i18n/context'
import { formatDateTime, formatDuration, formatRelative } from '@/i18n'
import {
  severityRank,
  severityTone,
  usageSeverityTone,
  sessionActivityTone,
  sessionStateTone,
  kindTone,
} from '@/lib/tone'
import {
  activityLabelKey,
  sessionActivity,
  sessionHasEnded,
  type SessionActivity,
} from '@/lib/sessionActivity'
import { riskSentence, clearableDeadPanes } from '@/lib/riskSentence'
import { useSystemStatus } from '@/hooks/useSystemStatus'
import {
  useAccountLabels,
  useModelLabels,
  useHostLabels,
  useAccountByProfile,
} from '@/hooks/usePoolLabels'
import { useElapsed, useNow } from '@/hooks/useElapsed'
import { useDisclosures } from '@/hooks/useDisclosures'
import { periodMinutesOf, resetsBefore } from '@/lib/quotaWindows'
import {
  LayoutTabs,
  SessionAge,
  SessionEnded,
  SortableSlot,
  WallControls,
  sessionLabel,
  useSessionDetail,
  useSessionsList,
  useSessionActivity,
  type SessionRow,
} from '@/terminal/wall'
import { PaneChordProvider } from '@/terminal/paneChord'
import { CommandPalette, type PaletteCommand } from '@/terminal/palette'
import { LastTurnSummary } from '@/terminal/lastTurn'
import { ShortcutsOverlay } from '@/terminal/shortcuts'
import { useSelectionMode } from '@/hooks/useSelectionMode'
import { usePhoneLayout } from '@/hooks/usePointerKind'
import { useDragAnnouncements, dragId, parseDragId } from '@/hooks/useManualOrder'
import { useWallLayout, usePresetLoad, type LayoutMode } from '@/hooks/useWallLayout'
import type { ID, SessionKind } from '@/types/domain'
import {
  useOverview,
  useResizableColumns,
  MetricRow,
  GpuRows,
  fmtGiB,
  dotTone,
  hostSampled,
  hostStale,
  hostLive,
  type OverviewHost,
  type OverviewData,
  type OverviewRisk,
} from './fleet-telemetry'
import './LiveOpsPage.css'
// The bulk bar is the fleet's, imported rather than copied so a third bar cannot
// drift from the other two. Accounts does the same.
import './HostsPage.css'

/* ------------------------------------------------------------ Wire shapes */

/**
 * One session, mirroring api.SessionResponse.
 *
 * Deliberately flat rather than `extends Meta`: this DTO is assembled field by
 * field and carries no `createdBy`, `updatedBy` or `deletedAt`, so inheriting
 * the envelope would declare three more fields that are never on the wire.
 *
 * The five decorated names this page used to add — taskTitle, accountLabel,
 * modelName, hostName, projectName — are gone. No handler joins them, so each
 * one was undefined and each fell through to the id fallback written beside it;
 * the session list has been showing raw ids while claiming to show names.
 */


/**
 * How many tiles a mode *draws*. Not how many the wall holds.
 *
 * These used to be capacities: switching to `single` truncated the wall to one
 * id and the other seven were gone for good — an operator who focused one
 * session to read it lost the arrangement they had built to get there. The wall
 * is now storage and this is a window onto it, so the modes are a way of looking
 * rather than a way of discarding.
 *
 * `grid` has no ceiling. Eight was a number with no relation to the screen; what
 * bounds a useful wall is whether a pane is still wide enough to read, which is
 * a floor on tile size (--k-pane-min-w/h) and not a count. A pane that is
 * scrolled out of view does not hold a socket, so the cost follows what is on
 * screen rather than what is on the wall.
 */
const VISIBLE_TILES: Record<LayoutMode, number> = {
  single: 1,
  split: 2,
  grid: Number.POSITIVE_INFINITY,
}

const ALL = '*'

/**
 * What the "Durum" filter offers.
 *
 * It used to list stored session states, and that was wrong twice over.
 *
 * It offered states nothing produces. Measured over the pool's whole history on
 * 2026-08-16: every session ever recorded is RUNNING, COMPLETED, FAILED or
 * TERMINATED. `IDLE` and `CHECKPOINTED` have never occurred — like the
 * `INPUT_REQUIRED` option removed before them, they could only ever empty the
 * list — while `TERMINATED`, the most common ending of all, was not offered.
 *
 * Worse, it filtered on a vocabulary the rows do not use. Every row is coloured
 * and labelled by its *activity* — working, waiting, idle, broken — which is
 * derived from the live signals, not stored. All four live sessions are stored
 * as RUNNING while two of them read as idle on screen, so an operator who saw
 * two amber rows, chose "IDLE" and got nothing was being shown a filter that
 * answers a different question from the list it filters.
 *
 * So the options are the words already on the rows, and *only* those:
 * `activityLabelKey` names both, which is what keeps the dropdown and the dot
 * from ever drifting apart again. A session that has finished is `ENDED` rather
 * than idle — the list keeps it for six hours, and "quiet, and may speak again"
 * is a different answer from "over". One that failed is found under `PROBLEM`,
 * because what an operator wants from a failure is not that it ended.
 */
const STATUS_FILTERS: SessionActivity[] = [
  'WORKING',
  'WAITING',
  // Between the two it sits between in meaning, and offered for the same reason
  // the others are: the array's contract is the words already on the rows and
  // only those. It is also the one filter that answers a real triage question —
  // which of these finished and is waiting on my next instruction.
  'DONE',
  'IDLE',
  'PROBLEM',
  'ENDED',
  'UNKNOWN',
]

/** What a kind means for what the operator can do with the session. The names
 *  are terms of art — an operator read "Yönetilen" on every row and every panel
 *  and could not tell what it was for. */
function kindHintKey(kind: SessionKind) {
  return kind === 'MANAGED'
    ? ('live.kindHint.managed' as const)
    : kind === 'ADOPTED'
      ? ('live.kindHint.adopted' as const)
      : ('live.kindHint.observed' as const)
}

function kindLabelKey(kind: SessionKind) {
  return kind === 'MANAGED'
    ? ('terminal.sessionKind.managed' as const)
    : kind === 'ADOPTED'
      ? ('terminal.sessionKind.adopted' as const)
      : ('terminal.sessionKind.observed' as const)
}

/** The label a session is known by. There is no title on the wire — the tmux
 *  name is the only human-chosen string a session carries, and the id is what
 *  is left when even that is absent. */


/* ------------------------------------------------------- Command-centre strip */

/* The overview wire types (OverviewHost/OverviewData/OverviewRisk) and the
   useOverview query now live in ./fleet-telemetry, shared with the fleet page. */

function usageKindLabel(kind: string, t: ReturnType<typeof useT>): string {
  return kind === 'session'
    ? t('account.usageWindow.session')
    : kind === 'weekly_all'
      ? t('account.usageWindow.weekly')
      : kind === 'monthly_all'
        ? t('account.usageWindow.monthly')
        : kind === 'weekly_scoped'
          ? t('account.usageWindow.weeklyScoped')
          : kind
}

/* ----------------------------------------------------------- Telemetry rail */

/**
 * The right-hand rail — what the operator needs while watching this session.
 *
 * It used to be an instrument cluster: seven hosts' CPU, RAM, disk and up to
 * four GPUs each, about thirty gauge rows, followed by a feed of raw event type
 * strings (`session.started · 2 dk`) with no subject and no name. Real numbers,
 * both of them, and neither answered a question anybody asks on this screen. The
 * fleet's gauges are the Hosts screen's whole job; a feed that cannot say *what*
 * started is not a feed.
 *
 * So the rail follows the terminal instead. When a session is open in the middle
 * column the rail is about that session: the one host it runs on, the one
 * account it spends, and only the risks that touch either. Under it, the
 * pool-wide things that can interrupt an operator whatever they are looking at —
 * who else is waiting, what else is at risk, and how much of each subscription
 * is left.
 */
function TelemetryRail({
  session,
  sessions,
  activityOf,
  onSelect,
}: {
  session?: SessionRow
  sessions: SessionRow[]
  activityOf: (s?: SessionRow) => SessionActivity
  onSelect: (id: string) => void
}) {
  const overview = useOverview()
  const o = overview.data
  const risks = o?.risks ?? []
  // Nothing starts folded. The rail is four answers at once and that is what it
  // is for; an upgrade that hid one of them would take information away from an
  // operator who never asked for it. What they fold themselves is remembered.
  const folds = useDisclosures('kimene.lo.rail')

  // The risks the focused session is standing on, so they are not read twice:
  // once as "this session's problem" and again in the pool list below.
  const focused = new Set(
    session
      ? risks
          .filter((r) => r.targetId === session.hostId || r.targetId === session.accountProfileId)
          .map(riskKey)
      : [],
  )

  return (
    <div className="k-railstack">
      {session && (
        <SessionContextPanel
          session={session}
          hosts={o?.hosts ?? []}
          accounts={o?.accounts ?? []}
          risks={risks.filter((r) => focused.has(riskKey(r)))}
          open={folds.isOpen('context')}
          onToggle={() => folds.toggle('context')}
        />
      )}
      <WaitingPanel
        sessions={sessions}
        activityOf={activityOf}
        focused={session?.id}
        onSelect={onSelect}
        open={folds.isOpen('waiting')}
        onToggle={() => folds.toggle('waiting')}
      />
      <RisksPanel
        risks={risks.filter((r) => !focused.has(riskKey(r)))}
        open={folds.isOpen('risks')}
        onToggle={() => folds.toggle('risks')}
      />
      <PoolQuotaPanel
        accounts={o?.accounts ?? []}
        open={folds.isOpen('quotas')}
        onToggle={() => folds.toggle('quotas')}
      />
    </div>
  )
}

/** Identity for a risk, which carries no id of its own: the detector that raised
 *  it plus the thing it was raised about. */
function riskKey(r: OverviewRisk): string {
  return `${r.kind}:${r.targetId ?? ''}:${r.params?.dependency ?? ''}`
}

/**
 * The session in the middle column, and what it is standing on.
 *
 * One host and one account rather than every host and every account. An operator
 * watching a session asks "can this one keep going" — answered by the disk of
 * the machine it writes to and the quota of the subscription it spends, and by
 * nothing at all about the other six machines.
 */
function SessionContextPanel({
  session,
  hosts,
  accounts,
  risks,
  open,
  onToggle,
}: {
  session: SessionRow
  hosts: OverviewHost[]
  accounts: OverviewData['accounts']
  risks: OverviewRisk[]
  open: boolean
  onToggle: () => void
}) {
  const t = useT()
  const host = hosts.find((h) => h.id === session.hostId)
  const account = accounts.find((a) => a.id === session.accountProfileId)
  // The window closest to binding, exactly as the pool panel picks one: an
  // account with four windows is limited by whichever fills first.
  const active = (account?.usage?.windows ?? []).filter((w) => w.isActive)
  const quota = active.length
    ? active.reduce((best, w) => (w.utilization > best.utilization ? w : best))
    : undefined

  return (
    <Panel
      title={sessionLabel(session, session.id)}
      summary={<span className="k-sctx__sub k-mono">{session.provider}</span>}
      open={open}
      onToggle={onToggle}
      className="k-rail__panel"
    >
      <div className="k-sctx">
        {host ? (
          <div className="k-sctx__block">
            <div className="k-sctx__blockhead">
              <StatusDot tone={dotTone(host)} live={hostLive(host)} label={host.status ?? ''} />
              <span className="k-sctx__name">{host.name}</span>
              {!hostSampled(host.metrics) ? (
                <Badge tone="unknown" subtle>
                  {t('live.metric.noSample')}
                </Badge>
              ) : hostStale(host.metrics) ? (
                <Badge tone="warn" subtle>
                  {t('live.metric.stale')}
                </Badge>
              ) : null}
            </div>
            <div className="k-hm__rows">
              <MetricRow label={t('live.metric.cpu')} pct={host.metrics.cpuUsedPercent} />
              <MetricRow
                label={t('live.metric.ram')}
                pct={host.metrics.memUsedPercent}
                detail={
                  host.metrics.memUsedBytes && host.metrics.memoryBytes
                    ? `${fmtGiB(host.metrics.memUsedBytes)} / ${fmtGiB(host.metrics.memoryBytes)}G`
                    : undefined
                }
              />
              <MetricRow label={t('live.metric.disk')} pct={host.metrics.diskUsedPercent} />
              {host.metrics.gpus && host.metrics.gpus.length > 0 && (
                <GpuRows gpus={host.metrics.gpus} t={t} />
              )}
            </div>
          </div>
        ) : (
          // The session names a host the overview does not carry. Saying so beats
          // drawing an empty gauge stack, which reads as a quiet machine.
          <EmptyState title={t('live.ctx.noHost')} />
        )}

        <div className="k-sctx__block">
          <div className="k-sctx__blockhead">
            <span className="k-sctx__name">{account?.label ?? t('live.ctx.noAccount')}</span>
          </div>
          {quota ? (
            <div className="k-quota__row">
              <div className="k-quota__head">
                <span className="k-quota__name">{usageKindLabel(quota.kind, t)}</span>
                <span className="k-mono k-quota__pct">{Math.round(quota.utilization)}%</span>
              </div>
              <Meter
                value={Math.min(1, quota.utilization / 100)}
                tone={usageSeverityTone(quota.severity, quota.utilization)}
                ariaLabel={account?.label ?? ''}
              />
            </div>
          ) : (
            // Unmeasured, not unlimited. A zero bar here would read as "plenty
            // left" about a subscription nobody has measured.
            <span className="k-sctx__unknown">{t('live.ctx.noQuota')}</span>
          )}
        </div>

        {risks.length > 0 && (
          <ul className="k-risks k-sctx__risks">
            {risks.map((r) => (
              <li key={riskKey(r)} className="k-risks__row">
                <StatusDot tone={severityTone(r.severity)} label={r.severity} />
                <span>{riskSentence(r, t)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  )
}

/**
 * Every session that has stopped and is waiting for a person.
 *
 * The one panel on this screen that is genuinely urgent, and the one thing the
 * rail had no answer for. Derived from the same [sessionActivity] the rows are
 * coloured by, so a session cannot pulse in the list and be missing here.
 *
 * The focused session is excluded: it is already the thing on screen, and
 * listing it under "these are waiting for you" would be telling an operator
 * about the terminal they are looking at.
 */
function WaitingPanel({
  sessions,
  activityOf,
  focused,
  onSelect,
  open,
  onToggle,
}: {
  sessions: SessionRow[]
  activityOf: (s?: SessionRow) => SessionActivity
  focused?: string
  onSelect: (id: string) => void
  open: boolean
  onToggle: () => void
}) {
  const t = useT()
  const waiting = sessions.filter((s) => s.id !== focused && activityOf(s) === 'WAITING')

  return (
    <Panel
      title={t('live.rail.waiting')}
      // In the header, so folding this section away cannot fold away the one
      // thing on the rail that is genuinely urgent. The count is the signal;
      // the list is the detail.
      summary={waiting.length > 0 ? <Badge tone="info">{String(waiting.length)}</Badge> : undefined}
      open={open}
      onToggle={onToggle}
      className="k-rail__panel"
    >
      {waiting.length === 0 ? (
        <p className="k-rail__none">{t('live.rail.noWaiting')}</p>
      ) : (
        <ul className="k-waitlist">
          {waiting.map((s) => (
            <li key={s.id}>
              <button type="button" className="k-waitlist__row" onClick={() => onSelect(s.id)}>
                <StatusDot tone="info" label={t('session.activity.waiting')} />
                <span className="k-waitlist__name">{sessionLabel(s, s.id)}</span>
                <WaitingFor session={s} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  )
}

/** How long this session has been stopped, counted forward in the browser from
 *  the age the server measured — so it keeps rising while nobody answers rather
 *  than freezing at whatever the last payload said. */
function WaitingFor({ session }: { session: SessionRow }) {
  const now = useNow(1000)
  const ago = session.waitingAgoMs ?? 0
  // Re-based whenever the server sends a new measurement, so the browser only
  // ever extrapolates between payloads instead of drifting away from them for
  // the life of the wait.
  const base = useRef({ at: now, ago })
  if (base.current.ago !== ago) base.current = { at: now, ago }
  return <span className="k-waitlist__age k-mono">{formatDuration(ago + (now - base.current.at))}</span>
}

interface QuotaRow {
  id: ID
  label: string
  /** Every window the provider published for this account, binding one first. */
  windows: UsageWindow[]
  /** The one closest to stopping the account, which is what the folded header
   *  reports and what the accounts are ordered by. */
  tightest: UsageWindow
}

/**
 * The pool's quotas: every window every account publishes, with what each one
 * costs in time.
 *
 * It used to show one window per account — the most-binding *active* one — and
 * that is a different panel from the one an operator needs. A subscription is
 * bound by three caps at once (a five-hour allowance, a weekly one, a weekly one
 * scoped to a model), and showing only whichever happened to be marked active
 * meant an account at 5% on its five-hour window read as comfortable while its
 * weekly sat at 90% three rows below the fold. Every window is published on
 * every read; nothing here is new data, only data that was being dropped.
 *
 * Each window carries two figures and, where it can be worked out, a third:
 * percent used, when it rolls over, and how many more times the short window
 * rolls over before the cap above it does. The third is derived — see
 * `lib/quotaWindows.ts` — and says so, because a projection drawn like a
 * measurement is exactly what this console refuses elsewhere.
 */
function PoolQuotaPanel({
  accounts,
  open,
  onToggle,
}: {
  accounts: OverviewData['accounts']
  open: boolean
  onToggle: () => void
}) {
  const t = useT()
  const { locale } = useLocale()
  const queryClient = useQueryClient()

  const rows: QuotaRow[] = []
  for (const a of accounts) {
    const windows = a.usage?.windows ?? []
    if (windows.length === 0) continue
    // Binding first, then fullest. An operator reads down this list until one
    // of them stops them, so the one that will is at the top.
    const ordered = [...windows].sort(
      (x, y) => Number(y.isActive) - Number(x.isActive) || y.utilization - x.utilization,
    )
    const tightest = ordered.reduce((best, w) => (w.utilization > best.utilization ? w : best))
    rows.push({ id: a.id, label: a.label, windows: ordered, tightest })
  }
  rows.sort((x, y) => y.tightest.utilization - x.tightest.utilization)

  /**
   * The oldest reading on screen.
   *
   * The rail shows what the worker's last sweep found, and a number with no age
   * on it invites an operator to treat a twenty-minute-old reading as now. The
   * *oldest* rather than the newest, because that is the one the whole panel can
   * honestly claim.
   */
  const oldest = accounts.reduce<string | undefined>((acc, a) => {
    const at = a.usage?.fetchedAt
    if (!at) return acc
    return acc === undefined || at < acc ? at : acc
  }, undefined)

  /**
   * Refreshing means re-reading from the provider where that is permitted, and
   * refetching either way.
   *
   * Probing needs `account.write`, which an Operator does not hold — so the
   * button promises a refresh and the timestamp reports what was achieved. A
   * refusal is not an error to show: the numbers still came back, they are just
   * as fresh as the last sweep made them, and the "as of" line already says so.
   */
  const refresh = useMutation({
    mutationFn: async () => {
      await Promise.all(
        rows.map((r) => api.post<unknown>(`/accounts/${r.id}/probe`, {}).catch(() => undefined)),
      )
      await queryClient.invalidateQueries({ queryKey: ['overview'] })
    },
  })

  const tightest = rows[0]?.tightest

  return (
    <Panel
      title={t('live.rail.quotas')}
      // What the panel says while it is folded: the fullest window in the pool.
      // The same figure the top bar carries, and for the same reason — a quota
      // an operator cannot see is one they plan around wrongly.
      summary={
        tightest && (
          <span className="k-mono k-panel__fig">{Math.round(tightest.utilization)}%</span>
        )
      }
      open={open}
      onToggle={onToggle}
      // Outside the disclosure button: a button inside a button is invalid, and
      // a re-read that also folded the panel would surprise every time.
      actions={
        <button
          type="button"
          className="k-quota__refresh"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          aria-label={t('live.rail.quotaRefresh')}
          title={t('live.rail.quotaRefresh')}
        >
          <Icon icon={RefreshCw} size={13} className={refresh.isPending ? 'k-spin' : undefined} />
        </button>
      }
      className="k-rail__panel"
    >
      {rows.length === 0 ? (
        <p className="k-rail__none">{t('live.rail.noQuotas')}</p>
      ) : (
        <div className="k-quota">
          {rows.map((r) => (
            <div key={r.id} className="k-quota__acct">
              <div className="k-quota__acctname">{r.label}</div>
              {r.windows.map((w) => (
                <QuotaWindowRow
                  // Kind and scope together: an account carries one weekly cap
                  // per model, so the kind alone is not unique.
                  key={`${w.kind}:${w.scopeModel ?? ''}`}
                  window={w}
                  siblings={r.windows}
                  label={r.label}
                />
              ))}
            </div>
          ))}
          {oldest && (
            <p className="k-quota__asof">
              {t('live.rail.quotaAsOf', { when: formatRelative(oldest, locale) })}
            </p>
          )}
        </div>
      )}
    </Panel>
  )
}

/**
 * One quota window: what it has taken, when it rolls over, and how many more of
 * it there is before the cap above it rolls over too.
 *
 * The third line is the only derived figure on this panel. It is marked with a
 * tilde, it is absent whenever anything it rests on is unknown rather than
 * guessed at, and its tooltip names the window length it was worked out from
 * and whether the provider published that length or this console assumed it.
 */
function QuotaWindowRow({
  window: w,
  siblings,
  label,
}: {
  window: UsageWindow
  siblings: UsageWindow[]
  label: string
}) {
  const t = useT()
  const { locale } = useLocale()
  const run = resetsBefore(siblings, w)
  const hours = run ? Math.round((periodMinutesOf(w) ?? 0) / 6) / 10 : 0

  return (
    <div className="k-quota__row">
      <div className="k-quota__head">
        <span className="k-quota__name">
          {usageKindLabel(w.kind, t)}
          {w.scopeModel && <span className="k-mono k-quota__scope"> · {w.scopeModel}</span>}
        </span>
        <span className="k-mono k-quota__pct">{Math.round(w.utilization)}%</span>
      </div>
      <Meter
        value={Math.min(1, w.utilization / 100)}
        tone={usageSeverityTone(w.severity, w.utilization)}
        ariaLabel={`${label} ${usageKindLabel(w.kind, t)}`}
      />
      {/* When it resets, which is the half of a quota an operator plans around:
          90% with an hour to go and 90% with six days to go are different
          situations.

          A window can arrive without one, and the two reasons are different
          enough to be worth separating. Claude writes `resets_at` only once a
          five-hour window has been opened, so an untouched account publishes the
          window at 0% with no reset at all — nothing is counting down because
          nothing has started. A window that *has* been used and still carries no
          reset is the provider declining to say. Drawing neither left a bar with
          a blank line under it, which reads as data Kimene lost. */}
      <div className="k-quota__foot">
        {w.resetAt ? (
          // Relative on screen because "six days" is what an operator plans
          // around, and the exact instant on hover because "six days" is not a
          // time anybody can put in a calendar. The display zone is applied at
          // the render, as everywhere: storage is UTC.
          <span className="k-quota__resets k-mono" title={formatDateTime(w.resetAt, locale)}>
            {t('live.rail.quotaResets', { when: formatRelative(w.resetAt, locale) })}
          </span>
        ) : (
          <span className="k-quota__idle">
            {t(w.utilization > 0 ? 'live.rail.quotaNoReset' : 'live.rail.quotaNotStarted')}
          </span>
        )}
      </div>
      {run && (
        <div
          // Sans, deliberately, beside a mono reset time. Mono is this console's
          // mark for a machine fact, and this line is the one thing on the panel
          // the provider did not say — it is Kimene's arithmetic. The face is how
          // an operator tells the two apart without reading the tooltip.
          className="k-quota__run"
          title={t(run.assumed ? 'live.rail.quotaRunAssumed' : 'live.rail.quotaRunMeasured', {
            hours: String(hours),
          })}
        >
          {t('live.rail.quotaRun', {
            count: String(run.count),
            kind: usageKindLabel(run.boundary.kind, t),
          })}
        </div>
      )}
    </div>
  )
}

/** Open risks — the real detector output. Empty is a positive state, shown as a
 *  green "all clear" line rather than a blank. */
function RisksPanel({
  risks,
  open,
  onToggle,
}: {
  risks: OverviewRisk[]
  open: boolean
  onToggle: () => void
}) {
  const t = useT()
  const queryClient = useQueryClient()
  // Clearing is one request for all of them, not one per pane: the operator is
  // answering a row that counts them, and a per-pane button would need a name
  // to put on it — which is the thing these sessions do not have.
  const clear = useMutation({
    mutationFn: async () => {
      await api.post<{ cleared: string[] }>('/sessions/unlisted/clear', {})
      await queryClient.invalidateQueries({ queryKey: ['overview'] })
    },
  })
  // The worst one still showing while the section is folded: a failed dependency
  // that only exists inside a collapsed panel is a signal nobody is spending.
  const worst = risks.reduce<OverviewRisk | undefined>(
    (best, r) => (best && severityRank(best.severity) >= severityRank(r.severity) ? best : r),
    undefined,
  )
  return (
    <Panel
      title={t('live.rail.risks')}
      summary={
        worst && (
          <span className="k-panel__fig">
            <StatusDot tone={severityTone(worst.severity)} label={worst.severity} />
            <span className="k-mono">{risks.length}</span>
          </span>
        )
      }
      open={open}
      onToggle={onToggle}
      className="k-rail__panel"
    >
      {risks.length === 0 ? (
        <div className="k-rail__ok">
          <StatusDot tone="ok" label="ok" />
          <span>{t('live.rail.noRisks')}</span>
        </div>
      ) : (
        <ul className="k-risks">
          {risks.map((r, i) => {
            // The one risk on this rail with something a person can press. The
            // steward clears these an hour after they leave the session list,
            // and an operator looking at the row is the one person that grace
            // was protecting — so the wait is theirs to skip. A live session
            // the console cannot show gets no button: it has no record, and
            // destroying work Kimene cannot account for is not an affordance.
            const clearable = clearableDeadPanes(r) > 0
            return (
              <li
                key={`${r.kind}-${i}`}
                className={clearable ? 'k-risks__row k-risks__row--act' : 'k-risks__row'}
              >
                <StatusDot tone={severityTone(r.severity)} label={r.severity} />
                <span className="k-risks__text">{riskSentence(r, t)}</span>
                <span className="k-mono k-risks__kind">{r.kind}</span>
                {clearable && (
                  <button
                    type="button"
                    className="k-risks__act"
                    onClick={() => clear.mutate()}
                    disabled={clear.isPending}
                    title={t('live.rail.clearUnlistedHint')}
                  >
                    {t('live.rail.clearUnlisted')}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {clear.isError && <Notice tone="warn">{t('live.rail.clearUnlistedFailed')}</Notice>}
    </Panel>
  )
}


/**
 * The per-row action menu, revealed as a kebab on hover so the row itself stays
 * full width and uncluttered. It carries the actions that used to be a single
 * inline stop button, and more: open the session in the terminal grid, show its
 * details, copy its id, and stop it (managed running sessions only).
 */
function SessionRowMenu({
  session,
  onOpen,
  onInspect,
  onRestart,
  onStop,
  onSwitchAccount,
}: {
  session: SessionRow
  onOpen: () => void
  onInspect: () => void
  onRestart: () => void
  onStop: () => void
  onSwitchAccount: () => void
}) {
  const t = useT()
  const managed = session.kind === 'MANAGED'
  const ended = sessionHasEnded(session.state)
  // Offered only where the server would allow it, and the server is the one
  // that decides: `safeBoundary` is its answer, published on the payload rather
  // than worked out again here. A second derivation of that rule is how a menu
  // comes to offer what the request then refuses — and the refusal would arrive
  // after the old session had been stopped.
  //
  // The payload can be a poll old, so the handler asks again; this only keeps
  // the menu honest in between.
  const switchable = managed && Boolean(session.safeBoundary)
  // Every managed session can be started again: a running one is replaced, an
  // ended one is retried. Only a session Kimene did not start has nothing here —
  // it belongs to whoever launched it.
  const restartable = managed
  // The same key ends a running session and clears away one that has already
  // ended. They are different acts and the row says which one it is offering:
  // "stop" on a corpse would promise an ending that already happened.
  const stoppable = managed && !ended
  const clearable = managed && ended
  return (
    <Menu as="div" className="k-slist__menu">
      <MenuButton
        className="k-slist__kebab"
        aria-label={t('common.actions')}
        title={t('common.actions')}
      >
        <Icon icon={MoreVertical} size={16} />
      </MenuButton>
      <MenuItems anchor="bottom end" className="k-actmenu">
        <MenuItem>
          <button type="button" className="k-actmenu__item" onClick={onOpen}>
            <Icon icon={Terminal} size={15} />
            {t('live.open')}
          </button>
        </MenuItem>
        <MenuItem>
          <button type="button" className="k-actmenu__item" onClick={onInspect}>
            <Icon icon={Info} size={15} />
            {t('action.details')}
          </button>
        </MenuItem>
        <MenuItem>
          <button
            type="button"
            className="k-actmenu__item"
            onClick={() => void navigator.clipboard?.writeText(session.id)}
          >
            <Icon icon={Copy} size={15} />
            {t('live.copyId')}
          </button>
        </MenuItem>
        {(restartable || stoppable || clearable) && (
          <div className="k-actmenu__sep" role="separator" />
        )}
        {restartable && (
          <MenuItem>
            <button type="button" className="k-actmenu__item" onClick={onRestart}>
              <Icon icon={RotateCcw} size={15} />
              {t('live.restart')}
            </button>
          </MenuItem>
        )}
        {managed && (
          <MenuItem>
            <button
              type="button"
              className="k-actmenu__item"
              onClick={onSwitchAccount}
              disabled={!switchable}
              // Disabled rather than hidden, with the reason on it: a control
              // that vanishes while an agent works teaches an operator that it
              // is unreliable, and they have no way to learn when it returns.
              title={switchable ? undefined : t('live.switchAccountBusy')}
            >
              <Icon icon={KeyRound} size={15} />
              {t('live.switchAccount')}
            </button>
          </MenuItem>
        )}
        {stoppable && (
          <MenuItem>
            <button
              type="button"
              className="k-actmenu__item k-actmenu__item--danger"
              onClick={onStop}
            >
              <Icon icon={Square} size={13} />
              {t('live.stop')}
            </button>
          </MenuItem>
        )}
        {clearable && (
          <MenuItem>
            <button
              type="button"
              className="k-actmenu__item k-actmenu__item--danger"
              onClick={onStop}
            >
              <Icon icon={Trash2} size={15} />
              {t('live.clear')}
            </button>
          </MenuItem>
        )}
      </MenuItems>
    </Menu>
  )
}

/* ------------------------------------------------------------------ Page */

export function LiveOpsPage() {
  const t = useT()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const { sessionId } = useParams<{ sessionId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const accountFilter = searchParams.get('account')

  const [kindFilter, setKindFilter] = useState<string>(ALL)
  const [stateFilter, setStateFilter] = useState<string>(ALL)
  const [starting, setStarting] = useState(false)
  // Which session is being moved to another account, if any.
  const [switching, setSwitching] = useState<string | null>(null)
  // Which session's details are open in the drawer. Null is closed — details are
  // on demand now, opened by clicking a terminal, not a panel always taking a
  // column of space under it.
  const [inspecting, setInspecting] = useState<string | null>(null)
  // The side columns collapse so the terminal can take the whole width; the
  // choice persists so an operator's chosen focus survives a reload.
  const [leftOpen, setLeftOpen] = useState(() => localStorage.getItem('kimene.lo.leftOpen') !== '0')
  const [rightOpen, setRightOpen] = useState(
    () => localStorage.getItem('kimene.lo.rightOpen') !== '0',
  )
  const { widths, startDrag } = useResizableColumns()

  // `?waiting=1` is the top bar's "N sessions are waiting for you" button
  // arriving here. It is consumed rather than kept: the parameter sets the
  // filter once and is then stripped, so the dropdown beneath is the only thing
  // holding that state afterwards and a URL cannot keep overruling what the
  // operator picks. A navigation is a human act, which is why applying it here
  // is not the prefill-overwrites-a-choice defect (CLAUDE.md) — nothing arrives
  // on its own.
  useEffect(() => {
    if (searchParams.get('waiting') !== '1') return
    setStateFilter('WAITING')
    const next = new URLSearchParams(searchParams)
    next.delete('waiting')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  useEffect(() => {
    localStorage.setItem('kimene.lo.leftOpen', leftOpen ? '1' : '0')
  }, [leftOpen])
  useEffect(() => {
    localStorage.setItem('kimene.lo.rightOpen', rightOpen ? '1' : '0')
  }, [rightOpen])
  const queryClient = useQueryClient()
  const accountLabels = useAccountLabels()
  const hostLabels = useHostLabels()
  // The host records, for the one question that needs more than a name: whether
  // an account's credentials resolve there. `isControlHost` and the account's
  // shared-storage flag are both part of that rule (accountReachableFrom).
  const hosts = useOverview().data?.hosts ?? []
  const apiError = useApiErrorMessage()

  // The list every session screen shares — one key, one cadence; see wall.tsx.
  const sessions = useSessionsList()

  const items = useMemo(() => sessions.data?.items ?? [], [sessions.data])

  /**
   * The arrangement: the wall, the list order, the selection and any named
   * layouts. Persisted, and pruned against `items` — the *unfiltered* list, so a
   * search box narrowing the rows cannot delete a pane.
   */
  const {
    layout,
    setLayout,
    panes,
    setPanes,
    selected,
    setSelected,
    manualOrder,
    setManualOrder,
    resetOrder,
    presets,
    activePreset,
    presetDirty,
    savePreset,
    loadPreset,
    deletePreset,
  } = useWallLayout(items)

  /**
   * On a phone the two columns are two *views*, not two columns.
   *
   * Stacked, they were a screen an operator scrolled: 478px of session list,
   * then a terminal below the fold that fought the page for the same vertical
   * drag. Neither half was usable and the half that matters — the terminal —
   * was the one you had to go looking for.
   *
   * So one at a time, and which one is state that moves on a *gesture* and on
   * nothing else: tapping a session goes to the terminal, the reveal control
   * comes back to the list. Anything continuously derived from the wall would be
   * the prefill defect (CLAUDE.md) — the wall changes on its own, through a deep
   * link, a restart swapping an id, a preset loading — and each of those would
   * take the operator to a view they did not ask for.
   *
   * The opening value is read from the route and the wall, once. That is not the
   * same thing: a URL naming a session *is* a request to look at that session,
   * and the wall is what this operator arranged last time, so a console left
   * with terminals open comes back to them. Both are answered at mount and never
   * again, which is what a lazy initialiser does and an effect does not.
   *
   * Separate from `leftOpen` rather than folded into it, so a phone never writes
   * to the column preference a desktop reads.
   */
  const phone = usePhoneLayout()
  const [phoneView, setPhoneView] = useState<'list' | 'terminal'>(
    sessionId || panes.length > 0 ? 'terminal' : 'list',
  )
  const listVisible = phone ? phoneView === 'list' : leftOpen
  const showList = useCallback(
    (on: boolean) => (phone ? setPhoneView(on ? 'list' : 'terminal') : setLeftOpen(on)),
    [phone],
  )
  /* The telemetry rail is a third column, and a phone has room for one. Forced
     rather than defaulted, and the stored preference is left untouched — so a
     desktop the same operator signs into keeps whatever they set there. */
  const railVisible = phone ? false : rightOpen

  const presetLoad = usePresetLoad(loadPreset)

  // The boxes are behind a toggle now; the toggle is what is permanent instead.
  const clearSelection = useCallback(() => setSelected([]), [setSelected])
  const { selecting, toggle: toggleSelecting } = useSelectionMode(selected, clearSelection)

  /**
   * Follows a restart everywhere the old id was written down.
   *
   * A restart mints a new session, so an id an operator arranged around stops
   * naming anything live. Done in one place because it has four places to reach:
   * the wall, the selection, the hand-made list order and the route.
   */
  const swapOnWall = useCallback(
    (from: string, to: string) => {
      setPanes((prev) =>
        prev.some((p) => p.id === from)
          ? prev.map((p) => (p.id === from ? { ...p, id: to } : p))
          : [...prev, { id: to, colSpan: 1, rowSpan: 1 }],
      )
      setSelected((prev) => prev.map((id) => (id === from ? to : id)))
      if (manualOrder?.includes(from))
        setManualOrder(manualOrder.map((id) => (id === from ? to : id)))
    },
    [setPanes, setSelected, manualOrder, setManualOrder],
  )

  const stop = useMutation({
    mutationFn: (id: string) => api.del<void>(`/sessions/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  })

  // A restart is a new session: the server stops the old one, launches the same
  // spec again and returns the replacement. The pane follows it, because an
  // operator who restarted a session is watching for it to come back and the id
  // they were attached to no longer names anything live.
  const restart = useMutation({
    mutationFn: (id: string) => api.post<SessionRow>(`/sessions/${id}/restart`, {}),
    onSuccess: (started, previousID) => {
      void queryClient.invalidateQueries({ queryKey: ['sessions'] })
      swapOnWall(previousID, started.id)
      navigate(`/live/${started.id}${accountFilter ? `?account=${accountFilter}` : ''}`)
    },
  })

  // One clock for the page: the row, the slot dot and the pane frame answer as
  // of the same moment. The derivation is shared with the dashboard (wall.tsx).
  const activityOf = useSessionActivity(sessions)

  // The kinds actually present, so the control above the list can be offered
  // only when it has something to choose between.
  const kinds = useMemo(() => {
    const seen = new Set<SessionKind>()
    for (const x of items) seen.add(x.kind)
    return [...seen]
  }, [items])

  const filtered = useMemo(() => {
    const rows = items.filter((s) => {
      if (kindFilter !== ALL && s.kind !== kindFilter) return false
      // Matched against what the row shows, not against what the record stores.
      if (stateFilter !== ALL && activityOf(s) !== stateFilter) return false
      if (accountFilter && s.accountProfileId !== accountFilter) return false
      return true
    })
    // The operator's own order, once they have made one.
    //
    // CLAUDE.md: a prefill loses to a human. The automatic sort below is a
    // prefill — a useful guess about what matters — and a hand-dragged order is a
    // decision, so nothing re-sorts behind it. Rows the stored order has never
    // seen (started since) go after the ones it has, rather than vanishing.
    // Nothing is lost by setting the guess aside: the pulse on the row, the count
    // in the top bar and the rail's waiting panel all still carry it.
    if (manualOrder) {
      const rank = new Map(manualOrder.map((id, i) => [id, i]))
      return rows
        .map((s, i) => ({ s, i, at: rank.get(s.id) ?? Number.MAX_SAFE_INTEGER }))
        .sort((a, b) => a.at - b.at || a.i - b.i)
        .map((r) => r.s)
    }
    // A session blocked on a person goes to the top. The pulse below is an
    // attention signal, and an attention signal under the fold is not one — with
    // enough sessions the row that needs answering is the one you scroll past.
    // Stable within each group, so the server's order is otherwise kept.
    return rows
      .map((s, i) => ({ s, i, waiting: activityOf(s) === 'WAITING' }))
      .sort((a, b) => Number(b.waiting) - Number(a.waiting) || a.i - b.i)
      .map((r) => r.s)
  }, [items, kindFilter, stateFilter, accountFilter, activityOf, manualOrder])

  // The route parameter is the source of truth for "which session am I looking
  // at"; the attached list is what the terminal grid renders. Keeping them in
  // step means a deep link opens with that session already attached.
  // A deep link puts its session on the wall. Appended, not prepended: the wall
  // order is the operator's and arriving by URL is not a reason to reshuffle it.
  useEffect(() => {
    if (!sessionId) return
    setPanes((prev) =>
      prev.some((p) => p.id === sessionId)
        ? prev
        : [...prev, { id: sessionId, colSpan: 1, rowSpan: 1 }],
    )
  }, [sessionId, setPanes])

  useEffect(() => {
    if (!inspecting) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setInspecting(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [inspecting])

  /**
   * Focus a session, and put it on the wall if it is not already there.
   *
   * The append is the fix for a reshuffle nobody asked for: this used to prepend,
   * so every click moved the clicked session to tile one and pushed every other
   * pane sideways. The panes never remounted — React keys are stable — but the
   * operator's eyes had to find each terminal again after every click.
   */
  const select = (id: string) => {
    navigate(`/live/${id}${accountFilter ? `?account=${accountFilter}` : ''}`)
    setPanes((prev) =>
      prev.some((p) => p.id === id) ? prev : [...prev, { id, colSpan: 1, rowSpan: 1 }],
    )
    // On a phone, picking a session *is* the request to look at it: this is the
    // gesture the view state moves on, and nothing else moves it, which is what
    // keeps a socket delta or a preset from taking the screen away.
    //
    // Guarded on `phone`, and the guard is the whole of it. `showList` folds the
    // *column* on a pointer, so calling it unconditionally meant every click on
    // a session row collapsed the sessions list on a desktop — the column an
    // operator keeps open all day, folding itself the first time they looked at
    // a terminal.
    if (phone) showList(false)
  }

  /* ----------------------------------------------------- command palette */

  const [palette, setPalette] = useState(false)
  const [keys, setKeys] = useState(false)

  /**
   * F2, from anywhere on this screen including inside a pane.
   *
   * The pane returns false for it so xterm never sends it on, but lets the DOM
   * event bubble — see TerminalPane. The SSH console has had this since it
   * shipped; the cockpit, which is the screen an operator actually lives on, had
   * no palette at all.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // F2, not F1: F1 is Help everywhere a keyboard has existed, and this
      // console had it opening a palette while its own key reference had no key
      // at all. The two swapped rather than one of them going without.
      if (e.key !== 'F2') return
      e.preventDefault()
      setPalette((open) => !open)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  /**
   * Everything this screen can do, as a list.
   *
   * Built from the same state the buttons read rather than a static table: this
   * page re-renders every second, and a palette entry holding a stale closure is
   * a control that lies about which session it names.
   */
  const commands = useMemo<PaletteCommand[]>(() => {
    const out: PaletteCommand[] = [
      { id: 'start', label: t('live.start'), group: t('palette.groupOpen'), run: () => setStarting(true) },
    ]
    for (const sn of filtered) {
      const name = sessionLabel(sn, sn.id)
      const onWall = panes.some((p) => p.id === sn.id)
      out.push({
        id: `show:${sn.id}`,
        label: `${name} — ${onWall ? t('palette.hide') : t('palette.show')}`,
        hint: t(activityLabelKey(activityOf(sn))),
        group: t('palette.groupPanes'),
        run: () =>
          onWall ? setPanes((prev) => prev.filter((p) => p.id !== sn.id)) : select(sn.id),
      })
      out.push({
        id: `inspect:${sn.id}`,
        label: `${name} — ${t('action.details')}`,
        group: t('palette.groupPanes'),
        run: () => setInspecting(sn.id),
      })
      out.push({
        id: `switch:${sn.id}`,
        label: `${name} — ${t('live.switchAccount')}`,
        group: t('palette.groupPanes'),
        run: () => setSwitching(sn.id),
      })
    }
    for (const preset of presets) {
      out.push({
        id: `preset:${preset.id}`,
        label: preset.name,
        hint: t('live.wallLayouts'),
        group: t('palette.groupLayouts'),
        run: () => loadPreset(preset.id),
      })
    }
    out.push(
      { id: 'view:single', label: t('live.layoutSingle'), group: t('palette.groupView'), run: () => setLayout('single') },
      { id: 'view:split', label: t('live.layoutSplit'), group: t('palette.groupView'), run: () => setLayout('split') },
      { id: 'view:grid', label: t('live.layoutGrid'), group: t('palette.groupView'), run: () => setLayout('grid') },
      { id: 'view:keys', label: t('keys.title'), group: t('palette.groupView'), run: () => setKeys(true) },
    )
    return out
  }, [filtered, panes, presets, activityOf, t, setPanes, setLayout, loadPreset])

  /* ------------------------------------------------------- drag and drop */

  /** A session's human name for an announcement, falling back to its id. */
  const nameOf = (id: string) => {
    const row = items.find((x) => x.id === id)
    return row ? sessionLabel(row, id) : id
  }


  /**
   * A drag has to be distinguishable from a click, because both surfaces it runs
   * on are made of buttons: the session rows open a session, and the pane headers
   * carry six controls. An 8px threshold is what separates "I pressed this" from
   * "I am moving this"; without it the first pixel of a shaky click starts a drag
   * and the click never lands.
   */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    // The reason a library is here at all. Nothing in this console could reorder
    // anything from the keyboard, and a wall an operator can only rearrange with
    // a mouse is a wall half the operators cannot rearrange.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  /**
   * Where a drop lands, for both surfaces and the crossing between them.
   *
   * The list and the wall are separate sortable contexts inside one DndContext, so
   * this reads what was picked up and what it was dropped over, and decides which
   * of three things happened. Dragging a row onto the wall is the third, and the
   * other half of the answer to "let us not do this one at a time".
   */
  const onDragEnd = useCallback(
    (e: DragEndEvent) => {
      const from = parseDragId(String(e.active.id))
      const over = e.over ? parseDragId(String(e.over.id)) : null
      if (!over || (from.surface === over.surface && from.id === over.id)) return

      // Reordering the wall. The array moves; nothing unmounts, because the tile
      // keys are session ids and no session changed.
      if (from.surface === 'wall' && over.surface === 'wall') {
        const a = panes.findIndex((p) => p.id === from.id)
        const b = panes.findIndex((p) => p.id === over.id)
        if (a < 0 || b < 0) return
        setPanes(arrayMove(panes, a, b))
        return
      }

      // A row dragged onto the wall: put it where it was dropped. The other half
      // of not doing this one session at a time.
      if (from.surface === 'list' && over.surface === 'wall') {
        if (panes.some((p) => p.id === from.id)) return
        const at = panes.findIndex((p) => p.id === over.id)
        const next = [...panes]
        next.splice(at < 0 ? next.length : at, 0, { id: from.id, colSpan: 1, rowSpan: 1 })
        setPanes(next)
        return
      }

      // Reordering the list. Committed as the whole filtered order rather than the
      // two rows that moved: a partial order would leave every other row to be
      // re-sorted behind it.
      if (from.surface !== 'list' || over.surface !== 'list') return
      const order = filtered.map((x) => x.id)
      const a = order.indexOf(from.id)
      const b = order.indexOf(over.id)
      if (a < 0 || b < 0) return
      setManualOrder(arrayMove(order, a, b))
    },
    [panes, setPanes, filtered, setManualOrder],
  )

  /** Said out loud, because a drag nobody can see is a drag nobody can follow.
   *  Shared with the fleet and the account pool: dnd-kit's own announcements are
   *  English and name raw ids. */
  const announcements = useDragAnnouncements(nameOf)

  const attached = useMemo(() => panes.map((p) => p.id), [panes])

  /**
   * The two sortable id lists, memoised.
   *
   * They were built inline in the JSX, and this page re-renders every second — the
   * activity light is a fact with a lifetime of seconds, so there is a clock. A
   * fresh array each tick made dnd-kit re-register its items mid-gesture and the
   * drag ended where it began, roughly whenever a drag lasted longer than a
   * render. It failed most often on the wall, because reaching a tile takes longer
   * than reaching a row, which is exactly the kind of intermittency that gets
   * called "sometimes drag does not work".
   */
  const listDragIds = useMemo(() => filtered.map((x) => dragId('list', x.id)), [filtered])
  const active = sessionId ?? attached[0] ?? null

  /**
   * Which tiles this mode draws, keeping the wall's own order.
   *
   * A window rather than a slice from the front, so focusing a session in
   * `single` shows the session you focused and not whichever pane happens to be
   * first. The wall itself is untouched — that is what makes switching modes
   * non-destructive.
   */
  const visible = useMemo(() => {
    const cap = VISIBLE_TILES[layout]
    if (panes.length <= cap) return panes
    const at = panes.findIndex((p) => p.id === active)
    if (at < 0) return panes.slice(0, cap)
    const start = Math.min(at, Math.max(0, panes.length - cap))
    return panes.slice(start, start + cap)
  }, [panes, layout, active])

  const wallDragIds = useMemo(() => visible.map((p) => dragId('wall', p.id)), [visible])
  /** The wall's seats, in the order it draws them: Cmd+S then a digit. Memoised
   *  for the reason the drag ids are — this page re-renders every second. */
  const seatIds = useMemo(() => visible.map((p) => p.id), [visible])

  // One line for both acts, because only one of them can be in flight from a
  // single row menu and an operator does not need to be told which mutation
  // object carried the refusal.
  const failedAction = restart.error ?? stop.error
  const actionError = failedAction ? apiError(failedAction).title : null

  return (
    <Page>
      {palette && <CommandPalette onClose={() => setPalette(false)} commands={commands} />}
      {keys && <ShortcutsOverlay onClose={() => setKeys(false)} />}
      <div className="k-lo">
        {/* The cockpit is the one screen with no page header — it is a wall of
            terminals and a title bar would cost a row of them. It still needs a
            level-one heading: this was the only screen in the console without
            one, so a screen-reader user arriving here had nothing naming where
            they were, and no first stop in the heading list. */}
        <h1 className="k-visually-hidden">{t('live.title')}</h1>
        {(accountFilter ||
          sessions.data?.hasMore ||
          actionError ||
          presetLoad.lost.length > 0) && (
          <div className="k-lo__notices">
            {/* A refused stop or restart said nothing at all before this: the
                menu closed, the row did not change, and the server's reason —
                "this account has no earlier conversation here to resume", "the
                host no longer exists" — went to the console's error handler and
                nowhere a person would look. */}
            {actionError && <Notice tone="err">{actionError}</Notice>}
            {presetLoad.lost.length > 0 && (
              <Notice tone="warn">
                {t('live.wallLost', {
                  count: presetLoad.lost.length,
                  names: presetLoad.lost.join(', '),
                })}{' '}
                <button type="button" className="k-notice__dismiss" onClick={presetLoad.dismiss}>
                  {t('action.close')}
                </button>
              </Notice>
            )}
            {accountFilter && (
              <Notice tone="accent">
                {t('account.title')} · <span className="k-mono">{accountFilter}</span>{' '}
                <Link to="/live">{t('common.clearFilters')}</Link>
              </Notice>
            )}
            {/* Every filter on this page runs in the browser, so a truncated
                first page is a filter that quietly under-reports. The server
                says when it cut the list; this says so too rather than letting
                "no matches" stand for the rows we happen to hold. */}
            {sessions.data?.hasMore && <Notice tone="warn">{t('live.truncated')}</Notice>}
          </div>
        )}

        <DndContext sensors={sensors} onDragEnd={onDragEnd} accessibility={{ announcements }}>
        <div
          className={clsx(
            'k-lo__cols',
            // Which view is on screen, for the stylesheet. The sessions column
            // is conditionally *rendered*; the wall is hidden with
            // `display: none` instead, because unmounting a pane costs a
            // WebSocket and a full scrollback replay to put back — so switching
            // views would reconnect every terminal on it.
            //
            // Only the list state is marked; there is no `--terminal` twin,
            // because nothing would read it and a class no stylesheet answers is
            // what `classNames.test.ts` exists to catch.
            phone && listVisible && 'k-lo__cols--list',
          )}
        >
          {/* -------- Sessions column -------- */}
          {!listVisible && (
            // The collapsed strip keeps both: reveal, and start. An action that
            // disappears because a panel was folded away is an action an
            // operator cannot rely on — which is the state the rail left it in.
            <div className="k-lo__stub">
              <button
                type="button"
                className="k-lo__reveal k-lo__reveal--start"
                onClick={() => setStarting(true)}
                aria-label={t('live.start')}
                title={t('live.start')}
              >
                <Icon icon={Plus} size={16} />
              </button>
              {/* The palette's door, and the only one this screen has.
                  F2 is not a door — it is not guessable, and a phone has no
                  function row at all, so the screen an operator lives on had a
                  command palette nothing could open.

                  Here rather than in the column header beside the other
                  controls, which is where the SSH console keeps its copy. That
                  header is measured at the column's 208px minimum and is full at
                  three controls (`bulk-sessions.spec.ts` pins it); a fourth
                  pushes one out. And this is the better half of the trade
                  anyway: the folded state is the terminal view on a phone, which
                  is where the palette is worth reaching for, while the open list
                  already has a search box and two filters above it. */}
              <button
                type="button"
                className="k-lo__reveal"
                onClick={() => setPalette(true)}
                aria-label={t('palette.open')}
                title={t('palette.open')}
              >
                <Icon icon={Search} size={16} />
              </button>
              <button
                type="button"
                className="k-lo__reveal k-lo__reveal--left"
                onClick={() => showList(true)}
                aria-label={t('live.showSessions')}
                title={t('live.showSessions')}
              >
                <Icon icon={PanelLeftOpen} size={16} />
              </button>
            </div>
          )}
          {listVisible && (
            <>
              <section className="k-lo__col k-lo__sessions" style={{ width: widths.left }}>
                <div className="k-lo__colhead">
                  <span className="k-lo__coltitle">{t('live.sessions')}</span>
                  <span className="k-lo__colcount k-mono">{filtered.length}</span>
                  {/* The permanent affordance for selection, in place of a
                      checkbox on every row. Icon-only and 28px, the same recipe as
                      the collapse control beside it. */}
                  <button
                    type="button"
                    className="k-lo__collapse"
                    aria-pressed={selecting}
                    onClick={toggleSelecting}
                    aria-label={t(selecting ? 'order.selectDone' : 'order.select')}
                    title={t(selecting ? 'order.selectDone' : 'order.select')}
                  >
                    <Icon icon={ListChecks} size={15} />
                  </button>
                  {/* Beside the toggle that revealed the boxes, and only while it
                      is on. It was in the bulk bar, which is on the other side of
                      the screen from the rows it selects; here it is next to the
                      count it changes. Only what is shown: the filters above are
                      the operator's, and reaching past them to select rows they
                      have hidden would act on something off screen. */}
                  {selecting && (
                    <button
                      type="button"
                      className="k-lo__collapse"
                      onClick={() => setSelected(filtered.map((x) => x.id))}
                      aria-label={t('order.selectAllShown')}
                      title={t('order.selectAllShown')}
                    >
                      <Icon icon={CheckCheck} size={15} />
                    </button>
                  )}
                  <button
                    type="button"
                    className="k-lo__collapse"
                    onClick={() => showList(false)}
                    aria-label={t('live.hideSessions')}
                    title={t('live.hideSessions')}
                  >
                    <Icon icon={PanelLeftClose} size={15} />
                  </button>
                </div>
                <div className="k-lo__view">
                  <WallControls
                    layout={layout}
                    onLayout={setLayout}
                    presets={presets}
                    onSave={(name) => savePreset(name, nameOf)}
                    onLoad={presetLoad.load}
                    onDelete={deletePreset}
                    canSave={panes.length > 0}
                    manualOrder={manualOrder}
                    onResetOrder={resetOrder}
                  />
                </div>
                {/* Full width rather than tucked into the header row. The column
                    is resizable down to 208px, where a header holding a title, a
                    count, this and a collapse control would truncate the one
                    that carries words. A row of its own reads at every width and
                    is where a list's "add" belongs. */}
                <Button
                  variant="primary"
                  size="sm"
                  icon={<Icon icon={Plus} size={15} />}
                  className="k-lo__start"
                  onClick={() => setStarting(true)}
                >
                  {t('live.start')}
                </Button>
                <div className="k-lo__filters">
                  {/* Offered only when there is more than one kind to choose
                      between. There never is: session records are created in
                      exactly two places and both write MANAGED, and `Adopt` —
                      the one function that would produce anything else — has no
                      caller and no route. So this control could only ever return
                      everything or nothing, which is what an operator meant by
                      not being able to use it.

                      It is hidden rather than deleted because the classification
                      behind it is real and in use (the steward reports unlisted
                      tmux sessions from it): the day adoption is wired up, a
                      second kind appears in the list and the control comes back
                      by itself.

                      Deliberately not done to the status filter beside it. A
                      kind is decided when a session is created and never moves;
                      an activity changes second to second, so hiding an option
                      that matches nothing *now* would make that dropdown flicker
                      under the operator's hand. */}
                  {kinds.length > 1 && (
                    <SelectField
                      label={t('live.sessionKind')}
                      value={kindFilter}
                      onChange={setKindFilter}
                      options={[
                        { value: ALL, label: t('common.all') },
                        ...kinds.map((k) => ({ value: k, label: t(kindLabelKey(k)) })),
                      ]}
                    />
                  )}
                  <SelectField
                    label={t('task.state')}
                    value={stateFilter}
                    onChange={setStateFilter}
                    options={[
                      { value: ALL, label: t('common.all') },
                      ...STATUS_FILTERS.map((a) => ({
                        value: a,
                        label: t(activityLabelKey(a)),
                      })),
                    ]}
                  />
                </div>
                <div className="k-lo__scroll">
                  <QueryState
                    pending={sessions.isPending}
                    error={sessions.error}
                    onRetry={() => void sessions.refetch()}
                    empty={filtered.length === 0}
                    emptyTitle={items.length === 0 ? t('live.empty') : t('common.noMatches')}
                    emptyHint={items.length === 0 ? t('live.emptyHint') : t('common.noMatchesHint')}
                  >
                    <SortableContext
                      // Prefixed. Both surfaces sort sessions, so both would
                      // otherwise register droppables under the same ids — and
                      // dnd-kit keys its registry by id, so a row and its tile
                      // were one draggable wearing two hats: every drop resolved
                      // to itself and nothing ever moved.
                      items={listDragIds}
                      strategy={verticalListSortingStrategy}
                    >
                      <ul className="k-slist">
                        {filtered.map((s) => {
                          const acct = s.accountProfileId
                            ? (accountLabels.get(s.accountProfileId) ?? s.accountProfileId)
                            : undefined
                          const activity = activityOf(s)
                          const tone = sessionActivityTone(activity)
                          const waiting = activity === 'WAITING'
                          return (
                            <SessionListItem
                              key={s.id}
                              id={s.id}
                              waiting={waiting}
                              selected={selected.includes(s.id)}
                              selecting={selecting}
                              onSelect={(on) =>
                                setSelected((prev) =>
                                  on ? [...prev, s.id] : prev.filter((x) => x !== s.id),
                                )
                              }
                              label={sessionLabel(s, s.id)}
                            >
                              <button
                                type="button"
                                className={clsx(
                                  'k-slist__row',
                                  `k-slist__row--${tone}`,
                                  active === s.id && 'k-slist__row--on',
                                )}
                                onClick={() => select(s.id)}
                                aria-current={active === s.id}
                              >
                                {/* The label carries both facts: what the colour means,
                              and the lifecycle state it does not replace. */}
                                <StatusDot
                                  tone={tone}
                                  live={activity === 'WORKING'}
                                  label={`${t(activityLabelKey(activity))} · ${s.state}`}
                                />
                                <span className="k-slist__main">
                                  <span className="k-slist__l1">
                                    <span className="k-slist__name">{sessionLabel(s, s.id)}</span>
                                    <SessionAge session={s} />
                                  </span>
                                  <span className="k-slist__l2">
                                    {/* Only when it is not the default. Every session
                                  in this pool's whole history is MANAGED — Kimene
                                  started it — so the badge announced the same word
                                  on every row forever and taught the eye to skip
                                  the line it sits on. ADOPTED and OBSERVED are the
                                  ones that change what an operator can do, and they
                                  are what this now marks. */}
                                    {s.kind !== 'MANAGED' && (
                                      <>
                                        <span
                                          className={`k-slist__kind k-slist__kind--${s.kind.toLowerCase()}`}
                                        >
                                          {t(kindLabelKey(s.kind))}
                                        </span>
                                        <span className="k-slist__sep">·</span>
                                      </>
                                    )}
                                    <span className="k-mono">{s.provider}</span>
                                    {acct && (
                                      <>
                                        <span className="k-slist__sep">·</span>
                                        <span className="k-slist__acct">{acct}</span>
                                      </>
                                    )}
                                    {/* An ended session stays listed for a few hours,
                                  because tmux keeps its pane and the reason it
                                  ended is still readable in it. The row has to
                                  say so in words: without this it differs from a
                                  live one only by the colour of a dot. */}
                                    <SessionEnded session={s} />
                                  </span>
                                </span>
                              </button>
                              <SessionRowMenu
                                session={s}
                                onOpen={() => select(s.id)}
                                onInspect={() => setInspecting(s.id)}
                                onSwitchAccount={() => setSwitching(s.id)}
                                onRestart={async () => {
                                  // Restarting something still running throws away
                                  // work in progress; restarting something that
                                  // ended throws away nothing. The confirmation
                                  // says which of the two this is.
                                  const running = !sessionHasEnded(s.state)
                                  const ok = await confirm({
                                    title: t('live.restart'),
                                    message: t(
                                      running
                                        ? 'live.restartConfirmRunning'
                                        : 'live.restartConfirmEnded',
                                    ),
                                    confirmLabel: t('live.restart'),
                                  })
                                  if (ok) restart.mutate(s.id)
                                }}
                                onStop={async () => {
                                  const ended = sessionHasEnded(s.state)
                                  const ok = await confirm({
                                    title: t(ended ? 'live.clear' : 'live.stop'),
                                    message: t(ended ? 'live.clearConfirm' : 'live.stopConfirm'),
                                    confirmLabel: t(ended ? 'live.clear' : 'live.stop'),
                                  })
                                  if (ok) stop.mutate(s.id)
                                }}
                              />
                            </SessionListItem>
                          )
                        })}
                      </ul>
                    </SortableContext>
                  </QueryState>
                </div>
              </section>

              <div
                className="k-lo__gutter"
                role="separator"
                aria-orientation="vertical"
                aria-label={t('live.resizeSessions')}
                onPointerDown={(e) => startDrag('left', e)}
              />
            </>
          )}

          {/* -------- Terminal column -------- */}
          <section className="k-lo__col k-lo__work" aria-label={t('live.terminal')}>
            {/* The saved arrangements, above the wall and spanning it. Here and
                not in the sessions column with the menu that makes them: this is
                how an operator moves between pieces of work, so it has to be in
                the same place whether that column is folded or not — the rule
                the layout switch itself was moved for, twice. */}
            <LayoutTabs
              presets={presets}
              activeId={activePreset}
              dirty={presetDirty}
              onLoad={presetLoad.load}
              onSave={(name) => savePreset(name, nameOf)}
              onDelete={deletePreset}
              canSave={panes.length > 0}
            />
            {/* Above the wall, spanning it: four buttons do not fit in a 288px
                sessions column, they wrap into a tower. Drawn only when there is
                a selection — the capability is advertised by the select-all box
                in the list header, which is always on screen, so a permanent bar
                here would only take height away from the terminals. */}
            {/* The view controls live in the sessions column now, where they cost
                no terminal height — but a control that folds away with a panel is
                one an operator cannot rely on, and this exact switch has been in
                that position once already. So when the column is folded they come
                back here rather than disappearing with it. */}
            {!listVisible && (
              <div className="k-wall__head">
                <WallControls
                  layout={layout}
                  onLayout={setLayout}
                  presets={presets}
                  onSave={(name) => savePreset(name, nameOf)}
                  onLoad={presetLoad.load}
                  onDelete={deletePreset}
                  canSave={panes.length > 0}
                  manualOrder={manualOrder}
                  onResetOrder={resetOrder}
                />
              </div>
            )}
            {selecting && (
              <SessionBulkBar
                sessions={items.filter((x) => selected.includes(x.id))}
                onClear={() => setSelected([])}
                onShowInGrid={(ids) => {
                  setPanes(ids.map((id) => ({ id, colSpan: 1, rowSpan: 1 })))
                  setLayout('grid')
                }}
                onSwapped={swapOnWall}
              />
            )}
            <SignInConfirm session={items.find((x) => x.id === active)} />
            <div className="k-lo__termwrap">
              <PaneChordProvider panes={seatIds} onPick={select}>
              <SortableContext items={wallDragIds} strategy={rectSortingStrategy}>
                <div className={`k-termregion k-termregion--${layout}`}>
                  {visible.length === 0 ? (
                    <div className="k-termregion__empty">
                      <EmptyState title={t('live.selectSession')} hint={t('live.emptyHint')} />
                    </div>
                  ) : (
                    visible.map((pane) => {
                      const id = pane.id
                      const session = items.find((x) => x.id === id)
                      return (
                        <SortableSlot
                          // Stable, and load-bearing. A terminal pane costs a
                          // WebSocket and a full scrollback replay to mount, so a
                          // key that changed with position would make reordering
                          // reconnect every pane it moved. Keyed by session, the
                          // reorder is a reorder of an array and nothing unmounts.
                          key={id}
                          pane={pane}
                          session={session}
                          activity={activityOf(session)}
                          onDetach={() => setPanes((prev) => prev.filter((x) => x.id !== id))}
                          onFocus={() => select(id)}
                          onResize={(colSpan, rowSpan) =>
                            setPanes((prev) =>
                              prev.map((x) => (x.id === id ? { ...x, colSpan, rowSpan } : x)),
                            )
                          }
                          // A toggle, because the header identity that carries it
                          // now says whether the drawer is open. A control that
                          // reports a pressed state it cannot leave is a lie the
                          // separate ⓘ button could get away with and this cannot.
                          onInspect={() => setInspecting((cur) => (cur === id ? null : id))}
                          inspecting={inspecting === id}
                          active={active === id}
                        />
                      )
                    })
                  )}
                </div>
              </SortableContext>
              </PaneChordProvider>
            </div>

          </section>

          {railVisible && (
            <>
              <div
                className="k-lo__gutter"
                role="separator"
                aria-orientation="vertical"
                aria-label={t('live.resizeRail')}
                onPointerDown={(e) => startDrag('right', e)}
              />

              {/* -------- Telemetry rail column -------- */}
              <aside
                className="k-lo__col k-lo__rail"
                style={{ width: widths.right }}
                aria-label={t('live.rail.waiting')}
              >
                {/* The rail header carries the layout switch. "Start a session"
                    used to sit here too and has moved to the sessions column: it
                    creates a row in that list, and an action belongs where its
                    effect lands. It was also stranded here — collapsing the rail
                    to give the terminal more width left an operator with no way
                    to start anything at all. */}
                {/* The layout switch used to live here, inside the rail's own
                    header — so folding the rail away took with it the only way to
                    change the layout. Same defect as "Start a session", two
                    hundred lines up, fixed the same way: the control belongs to
                    the thing it acts on, which is the terminal wall. */}
                <div className="k-lo__railhead">
                  <button
                    type="button"
                    className="k-lo__collapse"
                    onClick={() => setRightOpen(false)}
                    aria-label={t('live.hideRail')}
                    title={t('live.hideRail')}
                  >
                    <Icon icon={PanelRightClose} size={15} />
                  </button>
                </div>
                {/* Focusable because it scrolls and holds nothing focusable:
                    the rail is read-only panels, so without a tab stop of its
                    own a keyboard user could not scroll it at all. The label is
                    the aside's, which already names this region. */}
                <div
                  className="k-lo__scroll"
                  tabIndex={0}
                  role="group"
                  aria-label={t('live.rail.waiting')}
                >
                  <TelemetryRail
                    session={items.find((s) => s.id === active)}
                    sessions={items}
                    activityOf={activityOf}
                    onSelect={select}
                  />
                </div>
              </aside>
            </>
          )}
          {/* The rail's folded tab, and not on a phone: `railVisible` is forced
              false there, so this would be a control that opens a third column
              the screen has no room for. A button that cannot do the thing it
              names is worse than an absence. */}
          {!railVisible && !phone && (
            <button
              type="button"
              className="k-lo__reveal k-lo__reveal--right"
              onClick={() => setRightOpen(true)}
              aria-label={t('live.showRail')}
              title={t('live.showRail')}
            >
              <Icon icon={PanelRightOpen} size={16} />
            </button>
          )}

          {/* The details drawer overlays the columns, opened by clicking a
              terminal. It is on demand rather than a permanent panel, so the
              terminal keeps the whole middle column. */}
          {inspecting && (
            <InspectorDrawer sessionId={inspecting} onClose={() => setInspecting(null)} />
          )}
        </div>
        </DndContext>
      </div>

      {switching &&
        (() => {
          const s = items.find((x) => x.id === switching)
          if (!s) return null
          return (
            <SwitchAccountDialog
              sessionId={s.id}
              sessionName={sessionLabel(s, s.id)}
              provider={s.provider}
              host={hosts.find((h) => h.id === s.hostId)}
              hostName={hostLabels.get(s.hostId)}
              currentAccountId={s.accountProfileId}
              currentAccountLabel={
                s.accountProfileId ? accountLabels.get(s.accountProfileId) : undefined
              }
              onClose={() => setSwitching(null)}
              onSwitched={(id) => {
                setSwitching(null)
                void sessions.refetch()
                select(id)
              }}
            />
          )
        })()}

      {starting && (
        <StartSessionDialog
          onClose={() => setStarting(false)}
          onStarted={(id) => {
            setStarting(false)
            void sessions.refetch()
            select(id)
          }}
        />
      )}
    </Page>
  )
}

/* -------------------------------------------------------- Terminal slot */


/**
 * One row of the session list: a selector, a drag grip, and the row itself.
 *
 * The selector is the exception to the row being one big button, exactly as it is
 * on the fleet table — ticking a box is a different act from opening a session,
 * and a click that did both would make selecting several impossible without the
 * terminal following along behind. The grip is separate again, because a row that
 * dragged when you meant to click it is worse than no dragging at all.
 */
function SessionListItem({
  id,
  waiting,
  selected,
  selecting,
  onSelect,
  label,
  children,
}: {
  id: string
  waiting: boolean
  selected: boolean
  /** Whether the screen is offering its rows for selection. */
  selecting: boolean
  onSelect: (on: boolean) => void
  label: string
  children: ReactNode
}) {
  const t = useT()
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: dragId('list', id),
  })
  return (
    <li
      ref={setNodeRef}
      className={clsx(
        'k-slist__item',
        waiting && 'k-slist__item--waiting',
        selected && 'k-slist__item--sel',
        isDragging && 'k-slist__item--dragging',
      )}
      style={{
        // Inline, not a transition: prefers-reduced-motion flattens every
        // transition-duration to 0.01ms, so a transition-driven drag jumps.
        transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
      }}
    >
      {/* Both row-level controls at the leading edge, in the cell that stops a
          click from reaching the row.

          The grip used to be the last child, and the row menu's kebab overlays the
          *trailing* edge — absolutely positioned at right: 8px and 28px wide — so
          it covered 16 of the grip's 24px and left an 8px strip to grab. Worse,
          `opacity: 0` is still hit-testable, so the kebab held that ground on
          rows nobody was hovering. Here the two ends belong to one thing each, and
          it matches the fleet and the pool, which already keep grip and box in one
          cell with measured clearance.

          A span rather than a button, because Space and Enter are dnd-kit's start
          *and* end keys and a native button activates on them too — one press was
          seen twice and every drag ended where it began. */}
      <span className="k-slist__sel" onClick={(e) => e.stopPropagation()}>
        {/* One or the other, never both. Choosing rows and arranging them are two
            jobs, and an operator doing one is not doing the other; two 24px targets
            in a 28px cell only made each of them harder to hit. */}
        {selecting ? (
          <RowCheckbox checked={selected} onChange={onSelect} label={label} />
        ) : (
          <span
            className="k-slist__grip"
            aria-label={t('order.move', { name: label })}
            title={t('order.move', { name: label })}
            {...attributes}
            {...listeners}
          >
            <Icon icon={GripVertical} size={13} />
          </span>
        )}
      </span>
      {children}
    </li>
  )
}

/**
 * The bulk bar for a selection of sessions.
 *
 * Sequential, like the fleet's and the account pool's, and for a stronger reason
 * than either: a restart stops a CLI and starts another in the same working
 * directory, and firing eight of those at once is eight processes racing over
 * eight checkouts. The tally is the same vocabulary the other two bars use, so an
 * operator reads one shape everywhere.
 *
 * The skip is the interesting part. A restart mid-turn cuts a tool call in half,
 * which is what CLAUDE.md's safe-boundary rule exists to prevent — and unlike the
 * account switch, the restart endpoint does not refuse it. So the boundary is
 * honoured here: sessions that are working are counted as skipped, and the
 * confirmation says how many *before* the operator commits, rather than reporting
 * it afterwards. `safeBoundary` comes from the server on every session
 * (`withLiveActivity`), so the rule is read, not re-derived.
 */
function SessionBulkBar({
  sessions,
  onClear,
  onShowInGrid,
  onSwapped,
}: {
  sessions: SessionRow[]
  onClear: () => void
  onShowInGrid: (ids: string[]) => void
  onSwapped: (from: string, to: string) => void
}) {
  const t = useT()
  const confirm = useConfirm()
  const describe = useApiErrorMessage()
  const queryClient = useQueryClient()
  const [running, setRunning] = useState<'restart' | 'stop' | 'clear' | null>(null)
  const [progress, setProgress] = useState('')
  const [outcome, setOutcome] = useState<BulkOutcome | null>(null)

  const busy = running !== null
  const restartable = sessions.filter((s) => s.kind === 'MANAGED')
  const atBoundary = restartable.filter((s) => Boolean(s.safeBoundary))
  const live = sessions.filter((s) => s.kind === 'MANAGED' && !sessionHasEnded(s.state))
  const ended = sessions.filter((s) => s.kind === 'MANAGED' && sessionHasEnded(s.state))

  const run = async (
    kind: 'restart' | 'stop' | 'clear',
    targets: SessionRow[],
    skipped: number,
    each: (s: SessionRow) => Promise<void>,
  ) => {
    setRunning(kind)
    setOutcome(null)
    const tally: BulkOutcome = { ok: 0, skipped, failed: 0 }
    for (const [i, s] of targets.entries()) {
      setProgress(
        t('fleet.bulkProgress', {
          done: i + 1,
          total: targets.length,
          host: sessionLabel(s, s.id),
        }),
      )
      try {
        await each(s)
        tally.ok++
      } catch (err) {
        tally.failed++
        // Collected rather than thrown: the row menu's single shared error line
        // assumes one action in flight, which a batch is not.
        tally.detail = describe(err).title
      }
    }
    // Awaited, and that is the point. Fired and forgotten, the buttons came back
    // before the refetch landed, so `live` was still computed from the list as it
    // was *before* the batch — every row still reading as running. Pressing again
    // in that window sent the whole batch a second time, and it was seen doing it:
    // eight terminations at 08:46:24, the same eight again at 08:46:30.
    //
    // Nothing was harmed, because the API refuses to end a session twice — the
    // second call cleared away what had already ended and left the verdict alone.
    // But a bulk action that can be fired at a stale list is a bulk action that
    // will be, and restart is the same code path: there it would have restarted
    // the replacements.
    await queryClient.invalidateQueries({ queryKey: ['sessions'] })
    setRunning(null)
    setProgress('')
    setOutcome(tally)
  }

  const doRestart = async () => {
    const skipped = restartable.length - atBoundary.length
    const ok = await confirm({
      title: t('live.bulkRestart'),
      message:
        skipped > 0
          ? t('live.bulkRestartConfirmSkipping', { count: atBoundary.length, skipped })
          : t('live.bulkRestartConfirm', { count: atBoundary.length }),
      confirmLabel: t('live.bulkRestart'),
    })
    if (!ok) return
    await run('restart', atBoundary, skipped + (sessions.length - restartable.length), async (s) => {
      const started = await api.post<SessionRow>(`/sessions/${s.id}/restart`, {})
      // One place, at the end of each call, and no navigation: the row menu's
      // restart navigates on success, which across a batch means every call
      // fighting the last one for the route.
      onSwapped(s.id, started.id)
    })
  }

  const doStop = async () => {
    const ok = await confirm({
      title: t('live.bulkStop'),
      message: t('live.bulkStopConfirm', { count: live.length }),
      confirmLabel: t('live.bulkStop'),
    })
    if (!ok) return
    await run('stop', live, sessions.length - live.length, async (s) => {
      await api.del<void>(`/sessions/${s.id}`)
    })
  }

  const doClear = async () => {
    // Confirmed, because the single-row version is. The work is already over so
    // nothing is lost, but N rows disappearing from a list is still something an
    // operator should have meant to do.
    const ok = await confirm({
      title: t('live.bulkClear'),
      message: t('live.bulkClearConfirm', { count: ended.length }),
      confirmLabel: t('live.bulkClear'),
    })
    if (!ok) return
    await run('clear', ended, sessions.length - ended.length, async (s) => {
      await api.del<void>(`/sessions/${s.id}`)
    })
    onClear()
  }

  return (
    <div className="k-bulkbar" role="region" aria-label={t('fleet.bulkTitle')}>
      <span className={clsx('k-bulkbar__count', sessions.length === 0 && 'k-bulkbar__count--idle')}>
        {sessions.length === 0
          ? t('fleet.bulkNone')
          : t('fleet.bulkSelected', { count: sessions.length })}
      </span>
      <Button size="sm" disabled={busy} onClick={() => onShowInGrid(sessions.map((s) => s.id))}>
        {t('live.bulkShowInGrid')}
      </Button>
      <Button
        size="sm"
        busy={running === 'restart'}
        disabled={busy || atBoundary.length === 0}
        onClick={() => void doRestart()}
      >
        {t('live.bulkRestart')}
      </Button>
      <Button
        size="sm"
        busy={running === 'stop'}
        disabled={busy || live.length === 0}
        onClick={() => void doStop()}
      >
        {t('live.bulkStop')}
      </Button>
      <Button
        size="sm"
        busy={running === 'clear'}
        disabled={busy || ended.length === 0}
        onClick={() => void doClear()}
      >
        {t('live.bulkClear')}
      </Button>
      <Button size="sm" variant="ghost" disabled={busy} onClick={onClear}>
        {t('fleet.bulkClear')}
      </Button>
      {progress && <span className="k-bulkbar__progress">{progress}</span>}
      {outcome && (
        <span className={clsx('k-bulkbar__result', outcome.failed > 0 && 'k-bulkbar__result--err')}>
          {t('fleet.bulkDone', {
            ok: outcome.ok,
            skipped: outcome.skipped,
            failed: outcome.failed,
          })}
          {outcome.detail ? ` · ${outcome.detail}` : ''}
        </span>
      )}
    </div>
  )
}

/** Same shape the fleet and account bars report, so one reading serves all three. */
interface BulkOutcome {
  ok: number
  skipped: number
  failed: number
  detail?: string
}





/* ------------------------------------------------------------- Inspector */

/**
 * The details drawer. Slides in over the columns when a terminal is clicked and
 * closes on the X, on Escape (handled by the page), or on a click outside it.
 * Non-modal by design: the terminal underneath stays live, because reading what
 * a session is and watching what it does are the same task.
 */
function InspectorDrawer({ sessionId, onClose }: { sessionId: string; onClose: () => void }) {
  const t = useT()
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const el = ref.current
      if (!el) return
      const target = e.target as Node
      // A click on a terminal header (which switches the drawer to that session,
      // or closes it if it is already this one) must not also be read as
      // "outside, so close" — that would open and shut in the same gesture, and
      // it would undo the toggle. Those clicks are handled by their own
      // onInspect. The header is the pane's now; this named the slot's, which
      // no longer exists, so every one of these clicks was reaching onClose.
      if (el.contains(target)) return
      if ((target as HTMLElement).closest?.('.k-term__head')) return
      onClose()
    }
    // Deferred a frame so the click that opened the drawer does not immediately
    // close it.
    const id = window.setTimeout(() => document.addEventListener('mousedown', onDown), 0)
    return () => {
      window.clearTimeout(id)
      document.removeEventListener('mousedown', onDown)
    }
  }, [onClose])

  return (
    <div className="k-lo__drawer" role="dialog" aria-label={t('action.details')} ref={ref}>
      <Inspector sessionId={sessionId} onClose={onClose} />
    </div>
  )
}

function Inspector({ sessionId, onClose }: { sessionId: string; onClose?: () => void }) {
  const t = useT()
  const { locale } = useLocale()
  const accountLabels = useAccountLabels()
  const modelLabels = useModelLabels()
  const hostLabels = useHostLabels()

  const accountFor = useAccountByProfile()

  const detail = useSessionDetail(sessionId)
  const d = detail.data
  const elapsed = useElapsed(d?.startedAt, d?.endedAt)
  const account = d ? accountFor(d.accountProfileId) : undefined

  // Recomputed on a clock rather than only when a payload lands, for the reason
  // the list does it: the instant a payload arrives is the instant most
  // favourable to "working", and a light sampled only then would sit on green.
  const now = useNow(1000)
  const activity = sessionActivity({
    state: d?.state,
    kind: d?.kind,
    busyAgoMs: d?.busyAgoMs,
    waitingAgoMs: d?.waitingAgoMs,
    doneAgoMs: d?.doneAgoMs,
    reportsTurns: d?.reportsTurns,
    working: d?.working,
    staleMs: detail.dataUpdatedAt ? now - detail.dataUpdatedAt : 0,
    attached: d?.attached,
  })

  // A pool reference shown by name, with the id kept beneath as the machine
  // value it still is. A missing name (the account or model was removed) leaves
  // the id standing alone in mono rather than an empty cell; a missing id is the
  // em-dash every absent value uses.
  const named = (id: ID | undefined, name: string | undefined) => {
    if (!id) return '—'
    if (!name) return <span className="k-mono">{id}</span>
    return (
      <span className="k-named">
        {name}
        <span className="k-sub k-mono">{id}</span>
      </span>
    )
  }

  return (
    <Panel
      className="k-inspector"
      title={t('action.details')}
      actions={
        onClose && (
          <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('action.close')}>
            <Icon icon={X} size={15} />
          </Button>
        )
      }
    >
      <QueryState
        pending={detail.isPending}
        error={detail.error}
        onRetry={() => void detail.refetch()}
      >
        {d && (
          <>
            {/* Live status: what the agent is doing, and how long it has been at
                it. Turn/tool counts and token/context figures are deliberately
                NOT shown: Kimene never populates the first and cannot observe the
                second, and a zero the operator acts on is worse than an absence.

                The dot reads the *shared* activity, as every other dot in the
                console does. It used to read the stored state, so a session that
                had gone quiet showed amber in the list and a pulsing green here,
                at the same moment, about the same session — the exact
                disagreement lib/sessionActivity.ts exists to make impossible.
                The stored state stays beside it as a badge: it is a different
                fact, and the dot does not replace it. */}
            <div className="k-inspector__status">
              <StatusDot
                tone={sessionActivityTone(activity)}
                live={activity === 'WORKING'}
                label={`${t(activityLabelKey(activity))} · ${d.state}`}
              />
              <Badge tone={sessionActivityTone(activity)} subtle>
                {t(activityLabelKey(activity))}
              </Badge>
              <Badge tone={sessionStateTone(d.state)} subtle>
                {d.state}
              </Badge>
              {elapsed != null && (
                <span className="k-inspector__elapsed k-mono">
                  {d.state === 'RUNNING'
                    ? t('live.runningFor', { duration: formatDuration(elapsed) })
                    : t('live.ranFor', { duration: formatDuration(elapsed) })}
                </span>
              )}
            </div>

            {/* What the session last did, as the server read it from the
                transcript: the operator's last prompt, the agent's latest reply,
                the latest tool and how the turn's tools went. The drawer used to
                leave out exactly this, and it is the one thing an operator
                opening a quiet session wants to know. Absent when there is no
                transcript to read — which is said, not shown as nothing. */}
            <div className="k-inspector__lastturn">
              <span className="k-label">{t('session.lastTurn.title')}</span>
              <LastTurnSummary turn={d.lastTurn} />
            </div>

            {/* Quota — the account this session runs on, its real usage windows.
                Absent (not zeroed) when the provider publishes none. */}
            {account?.usage && (account.usage.windows?.length ?? 0) > 0 && (
              <div className="k-inspector__quota">
                <AccountUsagePanel usage={account.usage} label={account.label} />
              </div>
            )}

            <div className="k-inspector__summary">
              <KeyValue
                items={[
                  {
                    // Stated, always — a detail panel exists to answer what a
                    // session is, and a field that disappears makes an operator
                    // wonder what it would have said. But the badge is emphasis,
                    // and every session in this pool is MANAGED: a badge that is
                    // always lit is not a badge. So the default reads as plain
                    // text and the two kinds that change what the operator can
                    // do keep the emphasis.
                    key: 'kind',
                    label: t('live.sessionKind'),
                    value:
                      d.kind === 'MANAGED' ? (
                        t(kindLabelKey(d.kind))
                      ) : (
                        <Badge tone={kindTone(d.kind)}>{t(kindLabelKey(d.kind))}</Badge>
                      ),
                    // The word does not explain itself, and an operator asked
                    // what it meant. What it decides is whether Kimene can steer
                    // this session at all, which is worth a sentence.
                    hint: t(kindHintKey(d.kind)),
                  },
                  // The one fact that changes what this session may do to the
                  // machine it runs on, and it was answerable nowhere in the
                  // console — 14 of 43 sessions in this pool were launched with
                  // approvals skipped and the interface never said so. The
                  // record has carried it all along, which is why the launch
                  // block is kept on the session at all.
                  ...(d.launch?.bypassApprovals
                    ? [
                        {
                          key: 'approvals',
                          label: t('live.approvals'),
                          value: <Badge tone="warn">{t('live.approvalsBypassed')}</Badge>,
                        },
                      ]
                    : []),
                  ...(d.launch?.record
                    ? [
                        {
                          key: 'record',
                          label: t('live.recording'),
                          value: (
                            <Badge tone="info" subtle>
                              {t('live.recordingOn')}
                            </Badge>
                          ),
                        },
                      ]
                    : []),
                  // The session's own id, which used to sit in the pane header
                  // spending the console's scarcest width on eight characters of
                  // a value nobody reads aloud. It is still worth having — an
                  // operator correlating a log line or calling the API needs it —
                  // so it moved here, where the machine values already live,
                  // rather than being deleted outright.
                  { key: 'sessionId', label: t('live.sessionId'), value: d.id, mono: true },
                  { key: 'provider', label: t('account.provider'), value: d.provider, mono: true },
                  // Account and model are resolved to their names from the cached
                  // pool lists, id kept beneath. Host has no such list here, so it
                  // stays a mono id — honest rather than dressed up.
                  {
                    key: 'account',
                    label: t('task.account'),
                    value: named(
                      d.accountProfileId,
                      d.accountProfileId ? accountLabels.get(d.accountProfileId) : undefined,
                    ),
                  },
                  {
                    key: 'model',
                    label: t('task.model'),
                    value: named(d.modelId, d.modelId ? modelLabels.get(d.modelId) : undefined),
                  },
                  ...(d.projectName
                    ? [{ key: 'project', label: t('task.project'), value: d.projectName }]
                    : []),
                  ...(d.effort
                    ? [{ key: 'effort', label: t('live.effort'), value: d.effort, mono: true }]
                    : []),
                  // Named like the account and the model. It was a bare UUID,
                  // excused by a comment saying no host list was available here;
                  // there is one, and "which machine is this on" is the whole
                  // point of the row.
                  {
                    key: 'host',
                    label: t('task.host'),
                    value: named(d.hostId, hostLabels.get(d.hostId)),
                  },
                  {
                    key: 'tmux',
                    label: t('live.tmuxSession'),
                    value: d.tmuxSession ?? '—',
                    mono: true,
                  },
                  {
                    key: 'wd',
                    label: t('live.workingDir'),
                    value: d.workingDir ?? '—',
                    mono: true,
                  },
                  {
                    key: 'started',
                    label: t('agent.startedAt'),
                    value: d.startedAt ? formatDateTime(d.startedAt, locale) : '—',
                  },
                  ...(d.endedAt
                    ? [
                        {
                          key: 'ended',
                          label: t('live.endedAt'),
                          value: formatDateTime(d.endedAt, locale),
                        },
                      ]
                    : []),
                  // How it exited, when tmux observed a code. The list row says
                  // this and the panel an operator opens *for the detail* did
                  // not. Absent means none was seen — a session whose tmux
                  // vanished outright leaves no code at all — which is why this
                  // is conditional rather than a confident zero.
                  ...(d.exitStatus != null
                    ? [
                        {
                          key: 'exit',
                          label: t('live.exitLabel'),
                          value: String(d.exitStatus),
                          mono: true,
                        },
                      ]
                    : []),
                ]}
              />
            </div>

            <div className="k-inspector__body">
              <TimelinePanel sessionId={sessionId} />
            </div>
          </>
        )}
      </QueryState>
    </Panel>
  )
}

/**
 * The event timeline.
 *
 * It is the live stream filtered to this session, and only that. There is no
 * per-session event history endpoint and /sessions/{id} carries no `events`
 * array — this panel used to merge one in and always merged an empty list — so
 * what an operator sees here begins when the console connected. The panel says
 * so rather than presenting a short list as a complete one.
 */
function TimelinePanel({ sessionId }: { sessionId: string }) {
  const t = useT()
  const { locale } = useLocale()
  const { recentEvents } = useSystemStatus()

  const events = useMemo(
    () =>
      recentEvents
        .filter((e) => e.context.sessionId === sessionId)
        .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime()),
    [recentEvents, sessionId],
  )

  if (events.length === 0) {
    return <EmptyState title={t('cc.noEventsYet')} hint={t('live.timelineLiveOnly')} />
  }

  return (
    <>
      <Notice tone="info">{t('live.timelineLiveOnly')}</Notice>
      <ul className="k-evlist" aria-live="polite">
        {events.map((e) => (
          <li key={e.id} className="k-evlist__row">
            <span className="k-mono k-evlist__time">{formatDateTime(e.occurredAt, locale)}</span>
            <StatusDot tone={severityTone(e.severity)} label={e.severity} />
            <span className="k-mono k-evlist__type">{e.type}</span>
            <span className="k-evlist__src">{e.source.service}</span>
          </li>
        ))}
      </ul>
    </>
  )
}
